import { useState, useEffect, useCallback } from 'react';
import { insurancePartnerLinkAPI } from '../services/api';

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '4px' },
  infoBox: { backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#4338ca', marginBottom: '20px', lineHeight: '1.6' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  toolbar: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '16px', borderBottom: '1px solid #e2e8f0' },
  addButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'middle' },
  typeBadge: { display: 'inline-block', fontFamily: 'Monaco, Consolas, monospace', fontSize: '12px', color: '#6366f1', backgroundColor: '#eef2ff', padding: '4px 10px', borderRadius: '6px', textTransform: 'uppercase', fontWeight: '600' },
  urlText: { fontSize: '13px', color: '#3b82f6', wordBreak: 'break-all', maxWidth: '400px', display: 'inline-block' },
  statusBadge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' },
  activeStatus: { backgroundColor: '#dcfce7', color: '#166534' },
  inactiveStatus: { backgroundColor: '#fee2e2', color: '#991b1b' },
  actionButtons: { display: 'flex', gap: '8px' },
  iconButton: { padding: '8px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b', transition: 'all 0.15s ease' },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: '#64748b' },
  emptyText: { fontSize: '16px', fontWeight: '500', marginBottom: '8px' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '560px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#1e293b' },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1 },
  modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  hint: { fontSize: '12px', color: '#64748b', marginTop: '6px', lineHeight: '1.5' },
  select: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', backgroundColor: '#fff' },
  button: { padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none' },
  primaryButton: { backgroundColor: '#4f46e5', color: '#fff' },
  secondaryButton: { backgroundColor: '#f1f5f9', color: '#475569' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontSize: '14px' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px' },
};

const InsurancePartnerLinks = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ type: 'car', partnerName: '', url: '', logo: '', isActive: true });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchLinks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await insurancePartnerLinkAPI.getAll();
      setLinks(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch insurance partner links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const openCreateModal = () => {
    setEditing(null);
    setFormData({ type: 'car', partnerName: '', url: '', logo: '', isActive: true });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (link) => {
    setEditing(link);
    setFormData({
      type: link.type,
      partnerName: link.partnerName,
      url: link.url,
      logo: link.logo || '',
      isActive: link.isActive,
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.type || !formData.partnerName || !formData.url) {
      setFormError('Type, partner name, and URL are required');
      return;
    }
    try {
      setSaving(true);
      setFormError('');
      if (editing) {
        await insurancePartnerLinkAPI.update(editing.id, formData);
      } else {
        await insurancePartnerLinkAPI.create(formData);
      }
      setShowModal(false);
      fetchLinks();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (link) => {
    try {
      await insurancePartnerLinkAPI.toggleActive(link.id);
      fetchLinks();
    } catch {
      setError('Failed to toggle status');
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm(`Delete ${link.partnerName} (${link.type})?`)) return;
    try {
      await insurancePartnerLinkAPI.delete(link.id);
      fetchLinks();
    } catch {
      setError('Failed to delete');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Insurance Partner Links</h1>
          <p style={styles.subtitle}>Third-party insurance affiliate URLs shown on the website only</p>
        </div>
      </div>

      <div style={styles.infoBox}>
        <strong>How it works:</strong> Create one link per vehicle type (<code>car</code> or <code>bike</code>).
        Use <code>{'{regNo}'}</code> placeholder in the URL to inject the user's registration number dynamically.
        <br />
        <strong>Example:</strong> <code>https://twowheeler.policybazaar.com/question?regNo={'{regNo}'}</code>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.card}>
        <div style={styles.toolbar}>
          <button style={styles.addButton} onClick={openCreateModal}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 4v16m-8-8h16" /></svg>
            Add Partner Link
          </button>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Loading...</div>
        ) : links.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyText}>No insurance partner links yet</div>
            <div>Click "Add Partner Link" to create one for car or bike insurance.</div>
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Partner Name</th>
                <th style={styles.th}>URL</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.id}>
                  <td style={styles.td}>
                    <span style={styles.typeBadge}>{link.type}</span>
                  </td>
                  <td style={styles.td}>{link.partnerName}</td>
                  <td style={styles.td}>
                    <a href={link.url} target="_blank" rel="noopener noreferrer" style={styles.urlText}>{link.url}</a>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{ ...styles.statusBadge, ...(link.isActive ? styles.activeStatus : styles.inactiveStatus), cursor: 'pointer' }}
                      onClick={() => handleToggle(link)}
                      title="Click to toggle"
                    >
                      {link.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actionButtons}>
                      <button style={styles.iconButton} onClick={() => openEditModal(link)} title="Edit">
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button style={{ ...styles.iconButton, color: '#dc2626' }} onClick={() => handleDelete(link)} title="Delete">
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div style={styles.modal} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{editing ? 'Edit Partner Link' : 'Add Partner Link'}</h2>
              <button style={styles.iconButton} onClick={() => setShowModal(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div style={styles.modalBody}>
              {formError && <div style={styles.errorBox}>{formError}</div>}

              <div style={styles.formGroup}>
                <label style={styles.label}>Vehicle Type *</label>
                <select
                  style={styles.select}
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="car">Car</option>
                  <option value="bike">Bike</option>
                </select>
                <div style={styles.hint}>Unique per type. Only one active link per type is used on the website.</div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Partner Name *</label>
                <input
                  style={styles.input}
                  type="text"
                  value={formData.partnerName}
                  onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
                  placeholder="e.g. Policybazaar, Acko, Digit"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Affiliate URL *</label>
                <input
                  style={styles.input}
                  type="text"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://twowheeler.policybazaar.com/question?regNo={regNo}"
                />
                <div style={styles.hint}>
                  Use <code>{'{regNo}'}</code> placeholder where the registration number should be inserted.
                  If not present, it will be appended as <code>?regNo=...</code> query param.
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Logo URL (optional)</label>
                <input
                  style={styles.input}
                  type="text"
                  value={formData.logo}
                  onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span style={{ fontSize: '14px' }}>Active (visible on the website)</span>
                </label>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setShowModal(false)} disabled={saving}>
                Cancel
              </button>
              <button style={{ ...styles.button, ...styles.primaryButton, opacity: saving ? 0.6 : 1 }} onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsurancePartnerLinks;
